/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { App } from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useState } from 'react'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { formatShortDate } from '@sportninja/common/ts-utils/date-utils'
import {
  API_ACCESS_SUCCESS_MESSAGES,
  PLUGIN_SETUP_GUIDE_URL,
  type PluginKey,
} from '@sportninja/common/features/account/apiAccess'
import CopyComponent from 'src/components/CopyComponent'
import WarningModal from 'src/components/WarningModal'
import Button from 'src/components/ts-components/Button'
import DataGrid from 'src/components/ts-components/DataGrid'
import EmptyState from 'src/components/ts-components/EmptyState'
import { formatSuccessToast } from 'src/helpers/formatSuccessToast'
import { API_ACCESS_EMPTY_MIN_HEIGHT } from '../constants'
import { usePluginKeyMutations } from '../hooks/usePluginKeyMutations'
import { usePluginKeys } from '../hooks/usePluginKeys'
import { ApiAccessCard } from './ApiAccessCard'
import { ApiAccessSkeleton } from './ApiAccessSkeleton'
import PluginKeyModal from './PluginKeyModal'

interface PluginKeysSectionProps {
  accountUid: string
}

/** Which modal is open: none, the create form, or the edit form for one key. */
type ModalState =
  | { kind: 'closed' }
  | { kind: 'form'; pluginKey: PluginKey | null }

/**
 * The account's publishable Website Plugin keys. A key goes in the league's website
 * snippet and only shows this account's data, on the websites it lists.
 */
export const PluginKeysSection = ({ accountUid }: PluginKeysSectionProps) => {
  const { message } = App.useApp()
  const { data: pluginKeys, isLoading } = usePluginKeys(accountUid)
  const { revoke } = usePluginKeyMutations(accountUid)
  const [modal, setModal] = useState<ModalState>({ kind: 'closed' })
  const [revoking, setRevoking] = useState<PluginKey | null>(null)

  const confirmRevoke = async () => {
    if (!revoking) {
      return
    }
    try {
      await revoke.mutateAsync(revoking.id)
      message.success(
        formatSuccessToast(API_ACCESS_SUCCESS_MESSAGES.pluginKeyRevoked)
      )
      setRevoking(null)
    } catch (err) {
      message.error((err as Error)?.message)
    }
  }

  const columnDefs: ColDef<PluginKey>[] = [
    { headerName: 'Name', field: 'name', flex: 1, minWidth: 160 },
    {
      // Sized to the full key (see autoSizeStrategy): a truncated key can't be read or checked.
      headerName: 'Key',
      field: 'key',
      cellRenderer: KeyCell,
    },
    {
      headerName: 'Environment',
      field: 'environment',
      width: 130,
      valueFormatter: ({ value }) => capitalizeWords(value ?? ''),
    },
    {
      headerName: 'Websites',
      field: 'allowed_origins',
      flex: 2,
      minWidth: 200,
      valueFormatter: ({ value }) =>
        (value as string[] | undefined)?.join(', ') ?? '',
    },
    {
      headerName: 'Last Used',
      field: 'last_used_at',
      width: 140,
      valueFormatter: ({ value }) =>
        value ? formatShortDate(value, false) : 'Never',
    },
    {
      headerName: '',
      colId: 'actions',
      // Frozen to the right edge so Edit and Revoke stay reachable on a wide table.
      pinned: 'right',
      lockPinned: true,
      width: 100,
      sortable: false,
      resizable: false,
      cellRenderer: ActionsCell,
      cellRendererParams: {
        onEdit: (pluginKey: PluginKey) => setModal({ kind: 'form', pluginKey }),
        onRevoke: setRevoking,
      },
    },
  ]

  const renderBody = () => {
    if (isLoading && !pluginKeys) {
      return <ApiAccessSkeleton />
    }
    if (!pluginKeys?.length) {
      return (
        <EmptyState
          title={capitalizeWords('No Website Plugin Keys Yet')}
          minHeight={API_ACCESS_EMPTY_MIN_HEIGHT}
          subtitle='Create a key to show your schedules, standings and registrations on your website.'
        />
      )
    }
    return (
      <DataGrid<PluginKey>
        rowData={pluginKeys}
        columnDefs={columnDefs}
        getRowId={({ data }) => data.id}
        autoSizeStrategy={{ type: 'fitCellContents', colIds: ['key'] }}
        domLayout='autoHeight'
        autoHeightMinBodyHeight={0}
      />
    )
  }

  return (
    <ApiAccessCard
      title={capitalizeWords('Website Plugin Keys')}
      subtitle='Keys for the Website Plugin. Each key only shows this account’s data, on the websites you list.'
      action={
        <>
          <Button
            size='compact'
            variant='outlined'
            icon={<ExportOutlined />}
            text={capitalizeWords('Setup Guide')}
            onClick={() =>
              window.open(
                PLUGIN_SETUP_GUIDE_URL,
                '_blank',
                'noopener,noreferrer'
              )
            }
          />
          <Button
            size='compact'
            variant='primary'
            icon={<PlusOutlined />}
            text={capitalizeWords('Create Website Plugin Key')}
            onClick={() => setModal({ kind: 'form', pluginKey: null })}
          />
        </>
      }
    >
      {renderBody()}
      <PluginKeyModal
        isOpen={modal.kind === 'form'}
        onClose={() => setModal({ kind: 'closed' })}
        accountUid={accountUid}
        pluginKey={modal.kind === 'form' ? modal.pluginKey : null}
      />
      <WarningModal
        isOpen={revoking !== null}
        onClose={() => setRevoking(null)}
        onConfirm={confirmRevoke}
        title={capitalizeWords('Revoke Website Plugin Key')}
        description={`"${
          revoking?.name ?? ''
        }" will stop working on every website it is on. This cannot be undone.`}
        confirmLabel='REVOKE'
        isLoading={revoke.isPending}
        destructive
      />
    </ApiAccessCard>
  )
}

const KeyCell = ({ value }: ICellRendererParams<PluginKey, string>) =>
  value ? (
    <CopyComponent id={value} customText='KEY' alternativeBackground />
  ) : null

interface ActionsCellProps extends ICellRendererParams<PluginKey> {
  onEdit: (pluginKey: PluginKey) => void
  onRevoke: (pluginKey: PluginKey) => void
}

const ActionsCell = ({ data, onEdit, onRevoke }: ActionsCellProps) =>
  data ? (
    <div css={actionsStyle}>
      <Button
        size='compact'
        variant='outlined'
        iconOnly
        icon={<EditOutlined />}
        text={capitalizeWords('Edit Website Plugin Key')}
        onClick={() => onEdit(data)}
      />
      <Button
        size='compact'
        variant='outlined'
        iconOnly
        icon={<DeleteOutlined />}
        text={capitalizeWords('Revoke Website Plugin Key')}
        onClick={() => onRevoke(data)}
      />
    </div>
  ) : null

const actionsStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
`
